/**
 * Allocation Tracking React Query Hooks
 * Based on docs/spec/allocation/07-integration.md Section 8.3
 *
 * Provides data fetching and mutation hooks for allocation features.
 * Integrates with Zustand store for client-side state synchronization.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { allocationApi } from '../services/allocation';
import { useAllocationStore } from '../store/allocationStore';
// WP-ACCT-3: strategic-role assignments drive retirement attribution, so
// assignment mutations must invalidate the retirement net-worth query too.
import { retirementKeys } from './useRetirementPlanning';
import type {
  SetPortfolioTargetsRequest,
  SetBucketTargetsRequest,
  AssignAccountToBucketRequest,
  CreateAccountTargetRequest,
  UpdateAccountTargetRequest,
  ClassifyHoldingRequest,
  BulkClassifyHoldingsRequest,
  DismissWarningRequest,
  ApplyTemplateRequest,
  HoldingsListParams,
  WarningsListParams,
  // Historical allocation params
  HistoricalAllocationParams,
  PortfolioValueSeriesParams,
  PerformanceParams,
  DriftHistoryParams,
  AllocationComparisonParams,
  HoldingHistoryParams,
  // Manual holding CRUD types
  CreateHoldingRequest,
  UpdateHoldingRequest,
  // Tenant profile types (WP-ALLOC-CONFIG-1)
  TenantProfileCreate,
  TenantProfileUpdate,
} from '../types/allocation';

// ============================================================================
// Query Keys
// ============================================================================

export const allocationKeys = {
  all: ['allocation'] as const,

  // Portfolio
  portfolio: () => [...allocationKeys.all, 'portfolio'] as const,

  // Buckets
  buckets: () => [...allocationKeys.all, 'buckets'] as const,

  // Account Targets
  targets: () => [...allocationKeys.all, 'targets'] as const,
  target: (accountId: string) => [...allocationKeys.targets(), accountId] as const,

  // Holdings
  holdings: () => [...allocationKeys.all, 'holdings'] as const,
  holdingsList: (params?: HoldingsListParams) =>
    [...allocationKeys.holdings(), params ?? {}] as const,
  holding: (holdingId: string) => [...allocationKeys.holdings(), holdingId] as const,

  // Warnings
  warnings: () => [...allocationKeys.all, 'warnings'] as const,
  warningsList: (params?: WarningsListParams) =>
    [...allocationKeys.warnings(), params ?? {}] as const,

  // Templates
  templates: () => [...allocationKeys.all, 'templates'] as const,

  // Profile (WP-ALLOC-CONFIG-1)
  profile: () => [...allocationKeys.all, 'profile'] as const,

  // Sankey
  sankey: () => [...allocationKeys.all, 'sankey'] as const,

  // Historical Allocation (Time-Travel)
  historical: () => [...allocationKeys.all, 'historical'] as const,
  historicalAllocation: (params: HistoricalAllocationParams) =>
    [...allocationKeys.historical(), 'allocation', params] as const,
  valueSeries: (params: PortfolioValueSeriesParams) =>
    [...allocationKeys.historical(), 'value-series', params] as const,
  performance: (params: PerformanceParams) =>
    [...allocationKeys.historical(), 'performance', params] as const,
  driftHistory: (params: DriftHistoryParams) =>
    [...allocationKeys.historical(), 'drift', params] as const,
  comparison: (params: AllocationComparisonParams) =>
    [...allocationKeys.historical(), 'comparison', params] as const,
  holdingHistory: (holdingId: string, params: Omit<HoldingHistoryParams, 'holdingId'>) =>
    [...allocationKeys.historical(), 'holding', holdingId, params] as const,
};

// ============================================================================
// Portfolio Hooks
// ============================================================================

/**
 * Fetch current portfolio allocation
 */
export function usePortfolioAllocation() {
  const setPortfolio = useAllocationStore((state) => state.setPortfolio);

  return useQuery({
    queryKey: allocationKeys.portfolio(),
    queryFn: async () => {
      const response = await allocationApi.getPortfolioAllocation();
      setPortfolio(response.allocation);
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Set portfolio target allocations
 */
export function useSetPortfolioTargets() {
  const queryClient = useQueryClient();
  const setPortfolio = useAllocationStore((state) => state.setPortfolio);

  return useMutation({
    mutationFn: (data: SetPortfolioTargetsRequest) =>
      allocationApi.setPortfolioTargets(data),
    onSuccess: (response) => {
      setPortfolio(response.allocation);
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

// ============================================================================
// Bucket Hooks
// ============================================================================

/**
 * Fetch current bucket allocation
 */
export function useBucketAllocation() {
  const setBuckets = useAllocationStore((state) => state.setBuckets);

  return useQuery({
    queryKey: allocationKeys.buckets(),
    queryFn: async () => {
      const response = await allocationApi.getBucketAllocation();
      setBuckets(response.allocation);
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Set bucket target allocations
 */
export function useSetBucketTargets() {
  const queryClient = useQueryClient();
  const setBuckets = useAllocationStore((state) => state.setBuckets);

  return useMutation({
    mutationFn: (data: SetBucketTargetsRequest) =>
      allocationApi.setBucketTargets(data),
    onSuccess: (response) => {
      setBuckets(response.allocation);
      queryClient.invalidateQueries({ queryKey: allocationKeys.buckets() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

/**
 * Assign account to strategic buckets
 */
export function useAssignAccountToBucket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignAccountToBucketRequest) =>
      allocationApi.assignAccountToBucket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.buckets() });
      // WP-ACCT-3: a strategic role is now authoritative for retirement
      // attribution, so changing one changes retirement net worth. Without
      // this the user reassigns an account and sees the projection unchanged.
      queryClient.invalidateQueries({ queryKey: retirementKeys.netWorth() });
    },
  });
}

/**
 * Remove account from all bucket assignments
 */
export function useUnassignAccountFromBuckets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) =>
      allocationApi.unassignAccountFromBuckets(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.buckets() });
      // WP-ACCT-3: removing an assignment reverts the account to the subtype
      // default, which can also change retirement net worth.
      queryClient.invalidateQueries({ queryKey: retirementKeys.netWorth() });
    },
  });
}

// ============================================================================
// Account Target Hooks
// ============================================================================

/**
 * Fetch all account targets
 */
export function useAccountTargets() {
  const setAccountTargets = useAllocationStore((state) => state.setAccountTargets);

  return useQuery({
    queryKey: allocationKeys.targets(),
    queryFn: async () => {
      const response = await allocationApi.getAccountTargets();
      setAccountTargets(response.targets);
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create account target
 */
export function useCreateAccountTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAccountTargetRequest) =>
      allocationApi.createAccountTarget(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.targets() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

/**
 * Update account target
 */
export function useUpdateAccountTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      data,
    }: {
      accountId: string;
      data: UpdateAccountTargetRequest;
    }) => allocationApi.updateAccountTarget(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.targets() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

/**
 * Delete account target
 */
export function useDeleteAccountTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) =>
      allocationApi.deleteAccountTarget(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.targets() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

// ============================================================================
// Holdings Hooks
// ============================================================================

/**
 * Fetch holdings with classification info
 */
export function useHoldings(params?: HoldingsListParams) {
  return useQuery({
    queryKey: allocationKeys.holdingsList(params),
    queryFn: () => allocationApi.getHoldings(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch holdings for a specific account
 * Convenience hook for account-filtered holdings with synthetic support
 * @param accountId - Account ID to filter by (null to disable query)
 * @param includeSynthetic - Include synthetic holdings (default: true)
 */
export function useAccountHoldings(accountId: string | null, includeSynthetic = true) {
  const params: HoldingsListParams | undefined = accountId
    ? { accountId, includeSynthetic }
    : undefined;

  return useQuery({
    queryKey: allocationKeys.holdingsList(params),
    queryFn: () => allocationApi.getHoldings(params!),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Classify a single holding
 */
export function useClassifyHolding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      holdingId,
      data,
    }: {
      holdingId: string;
      data: ClassifyHoldingRequest;
    }) => allocationApi.classifyHolding(holdingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.holdings() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

/**
 * Bulk classify multiple holdings
 */
export function useBulkClassifyHoldings() {
  const queryClient = useQueryClient();
  const clearHoldingSelection = useAllocationStore(
    (state) => state.clearHoldingSelection
  );

  return useMutation({
    mutationFn: (data: BulkClassifyHoldingsRequest) =>
      allocationApi.bulkClassifyHoldings(data),
    onSuccess: () => {
      clearHoldingSelection();
      queryClient.invalidateQueries({ queryKey: allocationKeys.holdings() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

// ============================================================================
// Warning Hooks
// ============================================================================

/**
 * Fetch allocation warnings
 */
export function useAllocationWarnings(params?: WarningsListParams) {
  const setWarnings = useAllocationStore((state) => state.setWarnings);

  return useQuery({
    queryKey: allocationKeys.warningsList(params),
    queryFn: async () => {
      const response = await allocationApi.getWarnings(params);
      setWarnings(response.warnings);
      return response;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - warnings may update more frequently
  });
}

/**
 * Dismiss a warning
 */
export function useDismissWarning() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      warningId,
      data,
    }: {
      warningId: string;
      data?: DismissWarningRequest;
    }) => allocationApi.dismissWarning(warningId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

/**
 * Refresh warnings (trigger recalculation)
 */
export function useRefreshWarnings() {
  const queryClient = useQueryClient();
  const setWarnings = useAllocationStore((state) => state.setWarnings);

  return useMutation({
    mutationFn: () => allocationApi.refreshWarnings(),
    onSuccess: (response) => {
      setWarnings(response.warnings);
      queryClient.invalidateQueries({ queryKey: allocationKeys.warnings() });
    },
  });
}

// ============================================================================
// Template Hooks
// ============================================================================

/**
 * Fetch available templates
 */
export function useAllocationTemplates() {
  const setTemplates = useAllocationStore((state) => state.setTemplates);

  return useQuery({
    queryKey: allocationKeys.templates(),
    queryFn: async () => {
      const response = await allocationApi.getTemplates();
      setTemplates([...response.systemTemplates, ...response.customTemplates]);
      return response;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - templates change rarely
  });
}

/**
 * Apply a template
 */
export function useApplyTemplate() {
  const queryClient = useQueryClient();
  const setShowTemplateModal = useAllocationStore(
    (state) => state.setShowTemplateModal
  );

  return useMutation({
    mutationFn: ({
      templateId,
      data,
    }: {
      templateId: string;
      data: ApplyTemplateRequest;
    }) => allocationApi.applyTemplate(templateId, data),
    onSuccess: () => {
      setShowTemplateModal(false);
      // Invalidate all allocation data since template affects everything
      queryClient.invalidateQueries({ queryKey: allocationKeys.all });
    },
  });
}

// ============================================================================
// Profile Hooks (WP-ALLOC-CONFIG-1)
// ============================================================================

/**
 * Fetch tenant's allocation profile
 * Returns null data if no profile exists (404)
 */
export function useProfile() {
  return useQuery({
    queryKey: allocationKeys.profile(),
    queryFn: async () => {
      try {
        return await allocationApi.getProfile();
      } catch (error) {
        // 404 means no profile yet - return null, not an error
        // Import ApiClientError at top if not already imported
        if (error && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry 404s (handled as null) or other client errors
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status: number }).status;
        if (status >= 400 && status < 500) return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Create tenant's allocation profile (first-time setup)
 * Note: Modal closing is handled by the component, not the hook
 */
export function useCreateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TenantProfileCreate) => allocationApi.createProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.profile() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
    },
  });
}

/**
 * Update tenant's allocation profile
 * Note: Modal closing is handled by the component, not the hook
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TenantProfileUpdate) => allocationApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.profile() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
    },
  });
}

// ============================================================================
// Sankey Visualization Hook
// ============================================================================

/**
 * Fetch Sankey diagram data
 */
export function useAllocationSankey() {
  return useQuery({
    queryKey: allocationKeys.sankey(),
    queryFn: () => allocationApi.getSankeyData(),
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Historical Allocation Hooks (Time-Travel Queries)
// ============================================================================

/**
 * Fetch historical allocation at a specific date
 * @param params - Target date and optional account filter
 */
export function useHistoricalAllocation(params: HistoricalAllocationParams) {
  return useQuery({
    queryKey: allocationKeys.historicalAllocation(params),
    queryFn: () => allocationApi.getHistoricalAllocation(params),
    staleTime: 10 * 60 * 1000, // 10 minutes - historical data doesn't change
    enabled: !!params.targetDate,
  });
}

/**
 * Fetch portfolio value time series for charts
 * @param params - Date range and granularity
 */
export function usePortfolioValueSeries(params: PortfolioValueSeriesParams) {
  return useQuery({
    queryKey: allocationKeys.valueSeries(params),
    queryFn: () => allocationApi.getPortfolioValueSeries(params),
    staleTime: 10 * 60 * 1000,
    enabled: !!params.startDate && !!params.endDate,
  });
}

/**
 * Calculate portfolio performance between two dates
 * @param params - Start and end dates
 */
export function usePortfolioPerformance(params: PerformanceParams) {
  return useQuery({
    queryKey: allocationKeys.performance(params),
    queryFn: () => allocationApi.getPerformance(params),
    staleTime: 10 * 60 * 1000,
    enabled: !!params.startDate && !!params.endDate,
  });
}

/**
 * Fetch allocation drift history over time
 * @param params - Date range and granularity
 */
export function useDriftHistory(params: DriftHistoryParams) {
  return useQuery({
    queryKey: allocationKeys.driftHistory(params),
    queryFn: () => allocationApi.getDriftHistory(params),
    staleTime: 10 * 60 * 1000,
    enabled: !!params.startDate && !!params.endDate,
  });
}

/**
 * Compare allocation between two dates
 * @param params - Start and end dates
 */
export function useAllocationComparison(params: AllocationComparisonParams) {
  return useQuery({
    queryKey: allocationKeys.comparison(params),
    queryFn: () => allocationApi.getAllocationComparison(params),
    staleTime: 10 * 60 * 1000,
    enabled: !!params.startDate && !!params.endDate,
  });
}

/**
 * Fetch historical snapshots for a specific holding
 * @param params - Holding ID and date range
 */
export function useHoldingHistory(params: HoldingHistoryParams) {
  return useQuery({
    queryKey: allocationKeys.holdingHistory(params.holdingId, {
      startDate: params.startDate,
      endDate: params.endDate,
    }),
    queryFn: () => allocationApi.getHoldingHistory(params),
    staleTime: 10 * 60 * 1000,
    enabled: !!params.holdingId && !!params.startDate && !!params.endDate,
  });
}

// ============================================================================
// Manual Holding CRUD Hooks (WP-MANUAL-HOLD Phase 3)
// ============================================================================

/**
 * Create a new manual holding
 */
export function useCreateHolding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateHoldingRequest) => allocationApi.createHolding(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.holdings() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.buckets() });
    },
  });
}

/**
 * Fetch detailed holding information
 * @param holdingId - Holding ID (null to disable query)
 */
export function useHolding(holdingId: string | null) {
  return useQuery({
    queryKey: allocationKeys.holding(holdingId ?? ''),
    queryFn: () => allocationApi.getHolding(holdingId!),
    enabled: !!holdingId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update an existing manual holding
 */
export function useUpdateHolding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ holdingId, data }: { holdingId: string; data: UpdateHoldingRequest }) =>
      allocationApi.updateHolding(holdingId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.holding(variables.holdingId) });
      queryClient.invalidateQueries({ queryKey: allocationKeys.holdings() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
    },
  });
}

/**
 * Delete a manual holding
 */
export function useDeleteHolding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (holdingId: string) => allocationApi.deleteHolding(holdingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allocationKeys.holdings() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.portfolio() });
      queryClient.invalidateQueries({ queryKey: allocationKeys.buckets() });
    },
  });
}

// ============================================================================
// Combined Hooks
// ============================================================================

/**
 * Fetch all allocation data at once (for initial page load)
 */
export function useAllocationData() {
  const portfolioQuery = usePortfolioAllocation();
  const bucketsQuery = useBucketAllocation();
  const targetsQuery = useAccountTargets();
  const warningsQuery = useAllocationWarnings({ status: 'active' });

  return {
    portfolio: portfolioQuery,
    buckets: bucketsQuery,
    targets: targetsQuery,
    warnings: warningsQuery,
    isLoading:
      portfolioQuery.isLoading ||
      bucketsQuery.isLoading ||
      targetsQuery.isLoading ||
      warningsQuery.isLoading,
    isError:
      portfolioQuery.isError ||
      bucketsQuery.isError ||
      targetsQuery.isError ||
      warningsQuery.isError,
  };
}
